<script setup lang="ts">
import { computed } from 'vue';
import type { IdentityFieldDiff } from './caseDetail';
import CopyButton from './CopyButton.vue';

const props = defineProps<{
  subjectName: string;
  matchedName: string;
  subjectPhotoUrl?: string;
  matchPhotoUrl?: string;
  subjectPhotoFileId?: string;
  diff?: IdentityFieldDiff[];
}>();

const emit = defineEmits<{ open: [fileId: string] }>();

const markByStatus: Record<IdentityFieldDiff['status'], string> = {
  match: '=',
  partial: '≈',
  mismatch: '≠',
  unknown: '?',
};
const markStyles: Record<IdentityFieldDiff['status'], string> = {
  match: 'text-green-600',
  partial: 'text-amber-600',
  mismatch: 'text-red-600',
  unknown: 'text-gray-400',
};

// v4's literal header is "Identity comparison" plus this match/mismatch
// count — checked directly against the real source, which never puts a
// hit id or matched name in this header at all (the 2a-era "SUPPLIED
// INFORMATION vs {hitId}" heading this replaced was grounded in 2a's own
// design, not v4's; v4 supersedes it here like everywhere else it differs).
const matchCount = computed(() => props.diff?.filter((d) => d.status === 'match').length ?? 0);
const mismatchCount = computed(
  () => props.diff?.filter((d) => d.status === 'mismatch').length ?? 0
);
</script>

<template>
  <div
    class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
    data-test="identity-comparison"
  >
    <div class="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
      <h3 class="text-[13px] font-semibold text-gray-900">Identity comparison</h3>
      <span class="flex-1"></span>
      <span v-if="diff?.length" class="text-[11.5px] text-gray-400">
        {{ matchCount }} match · {{ mismatchCount }} mismatch
      </span>
    </div>

    <div class="flex flex-col gap-3 p-4 lg:flex-row">
      <!-- Different layouts per breakpoint. -->
      <div
        v-if="subjectPhotoUrl || matchPhotoUrl"
        class="flex flex-row gap-3 lg:flex-none lg:flex-col lg:gap-2"
      >
        <div v-if="subjectPhotoUrl" class="flex flex-col items-center gap-1">
          <button
            v-if="subjectPhotoFileId"
            type="button"
            data-test="subject-photo-button"
            class="h-20 w-16 flex-none overflow-hidden rounded border border-gray-200"
            @click="emit('open', subjectPhotoFileId)"
          >
            <img :src="subjectPhotoUrl" alt="Subject photo" class="h-full w-full object-cover" />
          </button>
          <img
            v-else
            :src="subjectPhotoUrl"
            alt="Subject photo"
            class="h-20 w-16 flex-none rounded border border-gray-200 object-cover"
          />
          <span class="max-w-16 truncate text-[10px] font-medium text-gray-400">{{
            subjectName
          }}</span>
        </div>
        <div v-if="matchPhotoUrl" class="flex flex-col items-center gap-1">
          <img
            :src="matchPhotoUrl"
            alt="Matched entity photo"
            class="h-20 w-16 flex-none rounded border border-gray-200 object-cover"
          />
          <span class="max-w-16 truncate text-[10px] font-medium text-gray-400">{{
            matchedName
          }}</span>
        </div>
      </div>

      <div v-if="diff?.length" class="flex min-w-0 flex-1 flex-col">
        <div class="flex gap-2">
          <span class="w-28 flex-none"></span>
          <span class="flex-1 text-[10px] font-semibold tracking-wide text-gray-400 uppercase"
            >Supplied</span
          >
          <span class="w-4 flex-none"></span>
          <span class="flex-1 text-[10px] font-semibold tracking-wide text-gray-400 uppercase"
            >On this hit</span
          >
        </div>
        <div
          v-for="row in diff"
          :key="row.field"
          class="flex items-baseline gap-2 border-t border-dotted border-gray-200 py-1.5 text-sm"
        >
          <span class="w-28 flex-none text-[11px] font-medium text-gray-500">{{ row.field }}</span>
          <span class="flex min-w-0 flex-1 items-baseline gap-1.5 break-words text-gray-900">
            {{ row.subject }}
            <CopyButton :value="row.subject" :label="`subject ${row.field}`" />
          </span>
          <span class="w-4 flex-none text-center font-semibold" :class="markStyles[row.status]">{{
            markByStatus[row.status]
          }}</span>
          <span class="flex min-w-0 flex-1 items-baseline gap-1.5 break-words text-gray-700">
            {{ row.matched }}
            <CopyButton :value="row.matched" :label="`matched ${row.field}`" />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
